import type { State } from './state';
import { initialState } from './state';
import type {
  StatefulDirectCall,
  StatefulRoom,
  AudioInputDeviceInfo,
  AudioOutputDeviceInfo,
  VideoInputDeviceInfo,
  StatefulLocalParticipant,
  StatefulRemoteParticipant,
} from './types';
import type { User as SBUser } from 'sendbird-calls';

/** Actions */
export type Action =
  | { type: 'AUTH'; payload: SBUser }
  | { type: 'DEAUTH' }
  | { type: 'ADD_CALL'; payload: StatefulDirectCall }
  | { type: 'RINGING'; payload: StatefulDirectCall }
  | { type: 'UPDATE_CALL'; payload: Partial<StatefulDirectCall> }
  | { type: 'CLEAR_CALLS' }
  | { type: 'ADD_ROOM'; payload: StatefulRoom }
  | { type: 'UPDATE_ROOM'; payload: Partial<StatefulRoom> }
  | {
      type: 'UPDATE_ROOM_LOCAL_PARTICIPANT';
      payload: { roomId: string; participant: Partial<StatefulLocalParticipant> };
    }
  | {
      type: 'UPSERT_ROOM_REMOTE_PARTICIPANT';
      payload: { roomId: string; participant: Partial<StatefulRemoteParticipant> };
    }
  | { type: 'DELETE_ROOM_REMOTE_PARTICIPANT'; payload: { roomId: string; participantId: string } }
  | { type: 'CLEAR_ROOMS' }
  | { type: 'UPDATE_AUDIO_INPUT_DEVICE_INFO'; payload: Partial<AudioInputDeviceInfo> }
  | { type: 'UPDATE_AUDIO_OUTPUT_DEVICE_INFO'; payload: Partial<AudioOutputDeviceInfo> }
  | { type: 'UPDATE_VIDEO_INPUT_DEVICE_INFO'; payload: Partial<VideoInputDeviceInfo> };

/** Reducer */
export const reducer = (prevState: State, action: Action): State => {
  switch (action.type) {
    case 'AUTH':
      return {
        ...prevState,
        user: action.payload,
      };
    case 'DEAUTH':
      return initialState;
    case 'ADD_CALL':
      return {
        ...prevState,
        calls: [...prevState.calls, action.payload],
      };
    case 'RINGING': {
      const isBusy = prevState.calls.some((call) => !call.isEnded);
      const call = action.payload;
      if (isBusy) {
        call.end();
      }
      return { ...prevState, calls: [...prevState.calls, call] };
    }
    case 'UPDATE_CALL': {
      const index = prevState.calls.findIndex((c) => c.callId === action.payload.callId);
      if (index === -1) {
        return prevState;
      }
      return {
        ...prevState,
        calls: [
          ...prevState.calls.slice(0, index),
          { ...prevState.calls[index], ...action.payload },
          ...prevState.calls.slice(index + 1),
        ],
      };
    }
    case 'CLEAR_CALLS':
      return { ...prevState, calls: initialState.calls };
    case 'ADD_ROOM':
      if (prevState.rooms.find((item) => item.roomId === action.payload.roomId)) {
        return prevState;
      }
      return { ...prevState, rooms: [...prevState.rooms, action.payload] };
    case 'UPDATE_ROOM': {
      const index = prevState.rooms.findIndex((c) => c.roomId === action.payload.roomId);
      if (index === -1) {
        return prevState;
      }
      return {
        ...prevState,
        rooms: [
          ...prevState.rooms.slice(0, index),
          { ...prevState.rooms[index], ...action.payload },
          ...prevState.rooms.slice(index + 1),
        ],
      };
    }
    case 'UPDATE_ROOM_LOCAL_PARTICIPANT': {
      const { roomId, participant } = action.payload;
      const roomIndex = prevState.rooms.findIndex((c) => c.roomId === roomId);
      if (roomIndex === -1) return prevState;
      const room = prevState.rooms[roomIndex];
      const pIndex = room.participants.findIndex(
        (p) => p.participantId === room.localParticipant.participantId
      );

      return {
        ...prevState,
        rooms: [
          ...prevState.rooms.slice(0, roomIndex),
          {
            ...room,
            localParticipant: { ...room.localParticipant, ...participant },
            participants: [
              ...room.participants.slice(0, pIndex),
              { ...room.participants[pIndex], ...participant },
              ...room.participants.slice(pIndex + 1),
            ],
          },
          ...prevState.rooms.slice(roomIndex + 1),
        ],
      };
    }
    case 'UPSERT_ROOM_REMOTE_PARTICIPANT': {
      const { roomId, participant } = action.payload;
      const roomIndex = prevState.rooms.findIndex((c) => c.roomId === roomId);
      if (roomIndex === -1) return prevState;
      const room = prevState.rooms[roomIndex];
      const index = room.remoteParticipants.findIndex(
        (p) => p.participantId === participant.participantId
      );
      const pIndex = room.participants.findIndex(
        (p) => p.participantId === participant.participantId
      );

      if (index === -1) {
        return {
          ...prevState,
          rooms: [
            ...prevState.rooms.slice(0, roomIndex),
            {
              ...room,
              remoteParticipants: [
                ...room.remoteParticipants,
                participant as StatefulRemoteParticipant,
              ],
              participants: [...room.participants, participant as StatefulRemoteParticipant],
            },
            ...prevState.rooms.slice(roomIndex + 1),
          ],
        };
      } else {
        return {
          ...prevState,
          rooms: [
            ...prevState.rooms.slice(0, roomIndex),
            {
              ...room,
              remoteParticipants: [
                ...room.remoteParticipants.slice(0, index),
                { ...room.remoteParticipants[index], ...participant },
                ...room.remoteParticipants.slice(index + 1),
              ],
              participants: [
                ...room.participants.slice(0, pIndex),
                { ...room.participants[pIndex], ...participant },
                ...room.participants.slice(pIndex + 1),
              ],
            },
            ...prevState.rooms.slice(roomIndex + 1),
          ],
        };
      }
    }
    case 'DELETE_ROOM_REMOTE_PARTICIPANT': {
      const { roomId, participantId } = action.payload;
      const roomIndex = prevState.rooms.findIndex((c) => c.roomId === roomId);
      if (roomIndex === -1) return prevState;
      const room = prevState.rooms[roomIndex];
      const index = room.remoteParticipants.findIndex((p) => p.participantId === participantId);
      if (index !== -1) {
        room.remoteParticipants.splice(index, 1);
      }

      const pIndex = room.participants.findIndex((p) => p.participantId === participantId);
      if (index !== -1) {
        room.participants.splice(pIndex, 1);
      }

      return {
        ...prevState,
        // spread the room object to create a new reference to it
        rooms: [
          ...prevState.rooms.slice(0, roomIndex),
          { ...room },
          ...prevState.rooms.slice(roomIndex + 1),
        ],
      };
    }
    case 'CLEAR_ROOMS':
      return { ...prevState, rooms: initialState.rooms };
    case 'UPDATE_AUDIO_INPUT_DEVICE_INFO':
      return {
        ...prevState,
        audioInputDeviceInfo: { ...prevState.audioInputDeviceInfo, ...action.payload },
      };
    case 'UPDATE_AUDIO_OUTPUT_DEVICE_INFO':
      return {
        ...prevState,
        audioOutputDeviceInfo: { ...prevState.audioOutputDeviceInfo, ...action.payload },
      };
    case 'UPDATE_VIDEO_INPUT_DEVICE_INFO':
      return {
        ...prevState,
        videoInputDeviceInfo: { ...prevState.videoInputDeviceInfo, ...action.payload },
      };
    default:
      return prevState;
  }
};
